import React, { useState } from 'react';
import { Head, Link, useForm, usePage } from '@inertiajs/react';
import DashboardLayout from '../../../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { 
  ArrowLeft, 
  Send, 
  Users, 
  Mail, 
  Settings,
  Trash2,
  Edit,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';

interface Subscriber {
  id: number;
  email: string;
  name: string | null;
  subscribed_at: string;
  status: 'active' | 'pending' | 'unsubscribed';
}

interface NewsletterData {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  access_type: 'open_access' | 'restricted_access';
  subscription_type: 'open_subscription' | 'auth_required';
  delivery_schedule: 'manual' | 'daily' | 'weekly' | 'monthly';
  is_active: boolean;
  require_confirmation: boolean;
  sender_name: string;
  sender_email: string;
  subscriber_count: number;
  created_at: string;
}

interface RecentIssue {
  id: number;
  subject: string;
  status: string;
  sent_at: string | null;
  recipient_count: number;
}

interface Issue {
  id: number;
  subject: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed';
  issue_number: number | null;
  scheduled_at: string | null;
  sent_at: string | null;
  created_at: string;
  ready_to_send: boolean;
  recipient_count: number;
  tracked_posts_count: number;
}

interface IssuesPagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

interface ShowProps {
  newsletter: NewsletterData;
  issues: Issue[];
  issues_pagination?: IssuesPagination;
  recent_issues: RecentIssue[];
  subscribers?: Subscriber[];
}

const Show: React.FC<ShowProps> = ({ newsletter, issues = [], issues_pagination, recent_issues, subscribers = [] }) => {
  const { url } = usePage();
  const [activeTab, setActiveTab] = useState('overview');
  
  // Initialize status filter from URL params
  const [statusFilter, setStatusFilter] = useState(() => {
    const params = new URLSearchParams(url.split('?')[1] || '');
    return params.get('status') || 'all';
  });
  const { data, setData, put, processing, errors } = useForm({
    name: newsletter.name,
    slug: newsletter.slug,
    description: newsletter.description || '',
    access_type: newsletter.access_type,
    subscription_type: newsletter.subscription_type,
    delivery_schedule: newsletter.delivery_schedule,
    is_active: newsletter.is_active,
    require_confirmation: newsletter.require_confirmation,
    sender_name: newsletter.sender_name,
    sender_email: newsletter.sender_email,
  });



  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    put(`/admin/newsletters/${newsletter.id}`);
  };



  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string | null): string => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" /> Active</Badge>;
      case 'pending':
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
      case 'unsubscribed':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" /> Unsubscribed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getIssueStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
      scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      sending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      sent: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    };
    return styles[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  };

  return (
    <DashboardLayout>
      <Head title={`${newsletter.name} - Newsletter`} />

      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/admin/newsletters">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-foreground">{newsletter.name}</h1>
                {newsletter.is_active ? (
                  <Badge className="bg-green-500">Active</Badge>
                ) : (
                  <Badge variant="secondary">Inactive</Badge>
                )}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {newsletter.subscriber_count} subscribers • Created {formatDate(newsletter.created_at)}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link href={`/admin/newsletters/${newsletter.id}/issues/new`}>
              <Button>
                <Send className="h-4 w-4 mr-2" />
                Create Issue
              </Button>
            </Link>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="issues">Issues</TabsTrigger>
            <TabsTrigger value="subscribers">Subscribers</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              {/* Newsletter Info */}
              <Card className="rounded-xl border bg-card text-card-foreground shadow">
                <CardHeader>
                  <CardTitle>Newsletter Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Access Type:</span>
                    <span className="font-medium">
                      {newsletter.access_type === 'open_access' ? 'Open Access (Public)' : 'Restricted'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subscription:</span>
                    <span className="font-medium">
                      {newsletter.subscription_type === 'open_subscription' ? 'Email-only' : 'Authentication Required'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Schedule:</span>
                    <span className="font-medium capitalize">{newsletter.delivery_schedule}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Confirmation:</span>
                    <span className="font-medium">
                      {newsletter.require_confirmation ? 'Required' : 'Not Required'}
                    </span>
                  </div>
                  {newsletter.sender_name && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Sender:</span>
                      <span className="font-medium">{newsletter.sender_name} ({newsletter.sender_email})</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Stats */}
              <Card className="rounded-xl border bg-card text-card-foreground shadow">
                <CardHeader>
                  <CardTitle>Statistics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-2xl font-bold">{newsletter.subscriber_count}</p>
                      <p className="text-sm text-muted-foreground">Total Subscribers</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-2xl font-bold">{recent_issues.length}</p>
                      <p className="text-sm text-muted-foreground">Sent Issues</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Issues */}
            <Card className="rounded-xl border bg-card text-card-foreground shadow">
              <CardHeader>
                <CardTitle>Recent Issues</CardTitle>
                <CardDescription>Newsletter issues preview.</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-center py-6 text-muted-foreground">
                  View all issues in the Issues tab.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Issues Tab */}
          <TabsContent value="issues" className="space-y-4">
            {/* Filters */}
            <div className="flex gap-2">
              {['all', 'draft', 'scheduled', 'sent', 'failed'].map((status) => (
                <button
                  key={status}
                  onClick={() => {
                    setStatusFilter(status);
                    const url = new URL(window.location.href);
                    url.searchParams.set('status', status);
                    window.location.href = url.toString();
                  }}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    statusFilter === status
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground hover:bg-muted/80'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>

            {/* Issues Table */}
            <Card className="rounded-xl border bg-card text-card-foreground shadow">
              <CardContent className="p-0">
                {issues.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-border">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Issue
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Recipients
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Posts Tracked
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Scheduled/Sent
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-card divide-y divide-border">
                        {issues.map((issue) => (
                          <tr key={issue.id} className="hover:bg-muted/50">
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-foreground">
                                #{issue.issue_number} - {issue.subject}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Created {formatDate(issue.created_at)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getIssueStatusBadge(issue.status)}`}>
                                {issue.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                              {issue.recipient_count}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                              {issue.tracked_posts_count}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                              {issue.scheduled_at ? (
                                <span className="text-blue-600 dark:text-blue-400">Scheduled: {formatDateTime(issue.scheduled_at)}</span>
                              ) : issue.sent_at ? (
                                <span className="text-green-600 dark:text-green-400">Sent: {formatDateTime(issue.sent_at)}</span>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <Link
                                href={`/admin/newsletters/${newsletter.id}/issues/${issue.id}`}
                                className="text-primary hover:text-primary/80 mr-4"
                              >
                                View
                              </Link>
                              {issue.status === 'draft' && (
                                <Link
                                  href={`/admin/newsletters/${newsletter.id}/issues/${issue.id}/edit`}
                                  className="text-muted-foreground hover:text-foreground"
                                >
                                  Edit
                                </Link>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    No issues found. Create your first issue to get started.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pagination */}
            {issues_pagination && issues_pagination.total_pages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Showing {((issues_pagination.current_page - 1) * issues_pagination.per_page) + 1} to {Math.min(issues_pagination.current_page * issues_pagination.per_page, issues_pagination.total_count)} of {issues_pagination.total_count} issues
                </p>
                <div className="flex gap-2">
                  {issues_pagination.current_page > 1 && (
                    <Link href={`/admin/newsletters/${newsletter.id}?page=${issues_pagination.current_page - 1}&status=${statusFilter}`}>
                      <Button variant="outline" size="sm">Previous</Button>
                    </Link>
                  )}
                  {issues_pagination.current_page < issues_pagination.total_pages && (
                    <Link href={`/admin/newsletters/${newsletter.id}?page=${issues_pagination.current_page + 1}&status=${statusFilter}`}>
                      <Button variant="outline" size="sm">Next</Button>
                    </Link>
                  )}
                </div>
              </div>
            )}

            {/* Create Issue Button */}
            <div className="flex justify-end">
              <Link href={`/admin/newsletters/${newsletter.id}/issues/new`}>
                <Button>
                  <Send className="h-4 w-4 mr-2" />
                  Create Issue
                </Button>
              </Link>
            </div>
          </TabsContent>

          {/* Subscribers Tab */}
          <TabsContent value="subscribers" className="space-y-4">
            <Card className="rounded-xl border bg-card text-card-foreground shadow">
              <CardHeader>
                <CardTitle>Subscribers</CardTitle>
                <CardDescription>Manage newsletter subscribers.</CardDescription>
              </CardHeader>
              <CardContent>
                {subscribers.length > 0 ? (
                  <div className="space-y-3">
                    {subscribers.map((subscriber) => (
                      <div key={subscriber.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">{subscriber.email}</p>
                          <p className="text-sm text-muted-foreground">
                            {subscriber.name || 'No name'} • Subscribed {formatDate(subscriber.subscribed_at)}
                          </p>
                        </div>
                        {getStatusBadge(subscriber.status)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-6 text-muted-foreground">
                    No subscribers yet. Share your newsletter to get started!
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <form onSubmit={handleUpdate}>
              <Card className="rounded-xl border bg-card text-card-foreground shadow">
                <CardHeader>
                  <CardTitle>Newsletter Settings</CardTitle>
                  <CardDescription>Update newsletter configuration.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      type="text"
                      value={data.name}
                      onChange={(e) => setData('name', e.target.value)}
                    />
                  </div>

                  {/* Slug */}
                  <div className="space-y-2">
                    <Label htmlFor="slug">Slug</Label>
                    <Input
                      id="slug"
                      type="text"
                      value={data.slug}
                      onChange={(e) => setData('slug', e.target.value)}
                    />
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={data.description}
                      onChange={(e) => setData('description', e.target.value)}
                    />
                  </div>

                  {/* Access Type */}
                  <div className="space-y-2">
                    <Label htmlFor="access_type">Access Type</Label>
                    <Select
                      value={data.access_type}
                      onValueChange={(value) => setData('access_type', value as 'open_access' | 'restricted_access')}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open_access">Open Access</SelectItem>
                        <SelectItem value="restricted_access">Restricted</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Subscription Type */}
                  <div className="space-y-2">
                    <Label htmlFor="subscription_type">Subscription Type</Label>
                    <Select
                      value={data.subscription_type}
                      onValueChange={(value) => setData('subscription_type', value as 'open_subscription' | 'auth_required')}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open_subscription">Open Subscription</SelectItem>
                        <SelectItem value="auth_required">Authentication Required</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Delivery Schedule */}
                  <div className="space-y-2">
                    <Label htmlFor="delivery_schedule">Delivery Schedule</Label>
                    <Select
                      value={data.delivery_schedule}
                      onValueChange={(value) => setData('delivery_schedule', value as 'manual' | 'daily' | 'weekly' | 'monthly')}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Manual</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Sender Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="sender_name">Sender Name</Label>
                      <Input
                        id="sender_name"
                        type="text"
                        value={data.sender_name}
                        onChange={(e) => setData('sender_name', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sender_email">Sender Email</Label>
                      <Input
                        id="sender_email"
                        type="email"
                        value={data.sender_email}
                        onChange={(e) => setData('sender_email', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Toggles */}
                  <div className="flex items-center justify-between space-y-2">
                    <div className="space-y-0.5">
                      <Label htmlFor="is_active">Active</Label>
                      <p className="text-xs text-muted-foreground">
                        Allow subscriptions to this newsletter
                      </p>
                    </div>
                    <Switch
                      id="is_active"
                      checked={data.is_active}
                      onCheckedChange={(checked) => setData('is_active', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between space-y-2">
                    <div className="space-y-0.5">
                      <Label htmlFor="require_confirmation">Require Confirmation</Label>
                      <p className="text-xs text-muted-foreground">
                        Send confirmation email before subscribing
                      </p>
                    </div>
                    <Switch
                      id="require_confirmation"
                      checked={data.require_confirmation}
                      onCheckedChange={(checked) => setData('require_confirmation', checked)}
                    />
                  </div>
                </CardContent>
                <CardFooter className="flex justify-end gap-2">
                  <Button type="submit" disabled={processing}>
                    <Edit className="h-4 w-4 mr-2" />
                    {processing ? 'Saving...' : 'Save Changes'}
                  </Button>
                </CardFooter>
              </Card>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Show;
